import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';
import Reveal from './motion/Reveal';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Frontend only - just show success message
    toast.success('Message sent! We\'ll be in touch soon.', {
      description: `Thanks ${formData.name}, we've received your message.`
    });
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'symbionic@gmail.com' },
    { icon: Phone, label: 'Phone', value: '(+61) 431 172 144' },
    { icon: MapPin, label: 'Location', value: 'Serving AU, NZ, US, CA, UK & IE' }
  ];

  return (
    <section id="contact" data-testid="contact-section" className="py-28 bg-ink relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute w-[36rem] h-[36rem] bg-cyan-500/10 rounded-full blur-[130px] -bottom-40 -left-40"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left content */}
          <Reveal>
            <div className="space-y-10">
              <div className="space-y-5">
                <p className="eyebrow text-cyan-400">Get in touch</p>
                <h2 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                  Let's talk
                </h2>
                <p className="text-lg md:text-xl text-slate-400 leading-relaxed">
                  Ready to protect what matters? Interested in partnering with Symbionic? 
                  We'd love to hear from you.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 group">
                    <div className="w-12 h-12 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center flex-shrink-0 transition-colors duration-300 group-hover:border-cyan-400/40">
                      <item.icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">{item.label}</h3>
                      <p className="text-slate-400">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <p className="text-slate-400 leading-relaxed">
                  <span className="text-white font-semibold">General guidance only.</span> We help you understand 
                  insurance — your broker or insurer remains the right place for specific advice.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Right form */}
          <Reveal delay={0.15}>
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-ink mb-2">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    data-testid="contact-input-name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="w-full px-4 py-3 rounded-lg border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-ink mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    data-testid="contact-input-email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className="w-full px-4 py-3 rounded-lg border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-ink mb-2">
                    Company
                  </label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    data-testid="contact-input-company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your company (optional)"
                    className="w-full px-4 py-3 rounded-lg border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-ink mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    data-testid="contact-input-message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your interest in VerifAEye or partnership opportunities..."
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border-slate-300 focus:border-cyan-500 focus:ring-cyan-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  data-testid="contact-submit-button"
                  className="group w-full bg-ink hover:bg-ink-soft text-white py-6 text-lg font-semibold rounded-full transition-all duration-300 hover:scale-[1.02]"
                >
                  Send Message
                  <Send className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>

                <p className="text-sm text-slate-500 text-center">
                  We look forward to hearing from you
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
